import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, signal, Signal, Type, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { IEnrollmentDetailItem } from '@lb/api/mylb/interfaces';
import { TwoColumnPageLayoutComponent } from '@lb/components/page-templates/two-column-page-layout/two-column-page-layout.component';
import { MDButtonGroupComponent, MDButtonToggleComponent, MDDialogService, MDHeaderComponent, MDTagComponent } from '@melodic-ui/components';
import { ComponentLoaderService } from '@melodic-ui/services';
import { PageHeaderComponent } from '@lb/components/page-header/page-header.component';
import { EnrollmentCardComponent } from '@lb/components/enrollment-card/enrollment-card.component';
import { LearnerEnrollmentCardComponent } from '../learner-enrollment-card.component';
import { PaginatorComponent } from '@lb/components/paginator/paginator.component';
import { PauseProgramDialogComponent } from '../dialogs/pause-program/pause-program-dialog.component';
import { ExtendedLeaveDialogComponent } from '../dialogs/extended-leave/extended-leave-dialog.component';
import { EnrollmentDetailService } from '@lb/api/mylb/services/enrollment-detail.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs';
import { LearnerUserStateService } from '../../../shared/state/learner-users-state.service';
import { NotificationsComponent } from '@lb/components/notifications/notifications.component';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { usesLessonDetailsV2 } from '@lb/functions';

@Component({
	selector: 'lrnr-enrollments-main',
	standalone: true,
	imports: [
		RouterModule,
		MDButtonGroupComponent,
		MDButtonToggleComponent,
		MDHeaderComponent,
		TwoColumnPageLayoutComponent,
		EnrollmentCardComponent,
		LearnerEnrollmentCardComponent,
		MDTagComponent,
		PaginatorComponent,
		NotificationsComponent
	],
	templateUrl: './enrollments-main.component.html',
	styleUrl: './enrollments-main.component.scss'
})
export class EnrollmentsMainComponent implements OnInit, OnDestroy, AfterViewInit {
	private _dialogService: MDDialogService = inject(MDDialogService);
	private _componentLoaderService: ComponentLoaderService = inject(ComponentLoaderService);
	private _enrollmentDetailService = inject(EnrollmentDetailService);
	private readonly appSettings = inject<IAppSettings>(APP_CONFIG);

	public isActiveEnrollmentsDisplayed = signal(true);

	private _details = signal<IEnrollmentDetailItem[]>([]);

	public activeEnrollments: Signal<IEnrollmentDetailItem[]> = computed(() => this._details().filter((e) => e.isActive));
	public inactiveEnrollments: Signal<IEnrollmentDetailItem[]> = computed(() => this._details().filter((e) => !e.isActive));

	public activeEnrollmentsDisabled: Signal<boolean> = computed(() => this.activeEnrollments().length === 0);
	public inactiveEnrollmentsDisabled: Signal<boolean> = computed(() => this.inactiveEnrollments().length === 0);

	public itemsPerPageOptions = [5, 10, 50];
	public itemsPerPage = signal(5);
	public currentPage = signal(1);

	protected readonly usesV2 = (enrollment: IEnrollmentDetailItem) => usesLessonDetailsV2(enrollment, this.appSettings);

	public lengthOfEnrollmentData = computed(() => {
		const data = this.isActiveEnrollmentsDisplayed() ? this.activeEnrollments() : this.inactiveEnrollments();

		return data.length;
	});

	public viewableEnrollmentData = computed(() => {
		const itemsPerPage = this.itemsPerPage();
		const currentPage = this.currentPage();

		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;

		const data = this.isActiveEnrollmentsDisplayed() ? this.activeEnrollments() : this.inactiveEnrollments();

		return data.slice(startIndex, endIndex);
	});

	@ViewChild('activeToggle')
	private _activeToggleComponent!: MDButtonToggleComponent;

	private _learnerUserStateService = inject(LearnerUserStateService);
	private _activeLearnerUserID = this._learnerUserStateService.activeLearnerUserID;
	constructor() {
		toObservable(this._activeLearnerUserID)
			.pipe(
				takeUntilDestroyed(),
				filter((aL) => Boolean(aL))
			)
			.subscribe((activeLearner) => {
				if (!activeLearner) {
					return;
				}

				this._enrollmentDetailService
					.getEnrollmentDetails({ learnerId: activeLearner })
					.pipe(take(1))
					.subscribe((details) => this._details.set(details));
			});
	}

	ngOnInit(): void {
		this._componentLoaderService.getContainer('banner-main-content').component.set(PageHeaderComponent);
		this._componentLoaderService.getContainer('banner-main-content').inputs.set({ pageTitle: `My Enrollments` });
	}

	ngAfterViewInit(): void {
		this._activeToggleComponent.onClick();
	}

	ngOnDestroy(): void {
		this._componentLoaderService.getContainer('banner-main-content').component.set(null);
	}

	displayInactiveEnrollments() {
		this.isActiveEnrollmentsDisplayed.set(false);
		this.currentPage.set(1);
	}

	displayActiveEnrollments() {
		this.isActiveEnrollmentsDisplayed.set(true);
		this.currentPage.set(1);
	}

	onPageChange(event: any) {
		this.currentPage.set(event);
	}

	onPageLengthChange(event: any) {
		this.itemsPerPage.set(event);
	}

	pauseEnrollment(): void {
		this._dialogService.open(PauseProgramDialogComponent as Type<Component>);
	}

	requestExtendedLeave(): void {
		this._dialogService.open(ExtendedLeaveDialogComponent as Type<Component>);
	}

	handleEnrollmentChange(changeType: string): void {
		if (changeType === 'pause-enrollment') {
			this.pauseEnrollment();
		} else if (changeType === 'request-extended-leave') {
			this.requestExtendedLeave();
		}
	}

}
